// src/components/scenes/VideoPlayerScene.js
// Filepath: // src/components/scenes/VideoPlayerScene.js

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Fade,
  Paper,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CloseIcon from "@mui/icons-material/Close";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { DEFAULT_TV_PREFS, fetchUserTvPrefs } from "../../services/userPrefsService";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers } from "../../input/remote";
import QRPlaceholder from "../common/QRPlaceholder";
import Focusable from "../common/Focusable";
import { useKeepScreenOn } from "../../hooks/useKeepScreenOn";

const CONTROLS_IDLE_MS = 2500;
const TOAST_MS = 900;
const SEEK_STEP = 5;

/** TV-friendly sizes (px) */
const TV_FS = {
  modalTitle: 56,
  modalBody: 24,
  modalHint: 20,
  toastLabel: 32,
  toastIcon: 64,
  cardMaxW: 960,
  timeFont: 28,
};

// TEMP toggle: disable intro modal and autoplay video
const DISABLE_INTERACTIVE_MODAL = true;

// Ramping thresholds in ms for x1, x2, x3, x4 while holding
const SEEK_RAMP_THRESHOLDS = {
  x2: 800,
  x3: 1600,
  x4: 2800,
};

export default function VideoPlayerScene() {
  useKeepScreenOn(true);

  const { scene, setScene, popScene, stack } = useScene();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const toastTimerRef = useRef(null);

  const [prefs, setPrefs] = useState(DEFAULT_TV_PREFS);

  const src = useMemo(
    () =>
      scene.params?.src ||
      "https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/720/Big_Buck_Bunny_720_10s_1MB.mp4",
    [scene.params?.src]
  );

  const previewMode = !!scene.params?.preview;
  const forceAutoplay = DISABLE_INTERACTIVE_MODAL || !!scene.params?.autoplay;
  const startMuted = !!scene.params?.muted || previewMode;

  // Resolve a "where should Back go" target
  const returnTo = useMemo(() => {
    const p = scene.params || {};
    if (p.backTo) return p.backTo;
    if (p.returnTo) return p.returnTo;
    if (p.item) return { name: "item", params: { item: p.item } };
    return null;
  }, [scene.params]);

  const [showIntro, setShowIntro] = useState(!DISABLE_INTERACTIVE_MODAL);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [time, setTime] = useState({ current: 0, duration: 0 });

  // Center action toast
  const [toast, setToast] = useState(null); // { key, label, icon }

  // Seek hold state for ramping behavior
  const seekHoldRef = useRef({
    active: false,
    dir: 0, // -1 rewind, 1 forward
    startTs: 0,
    lastMultiplier: 1,
  });

  const logPlayerEvent = useCallback((type, payload = {}) => {
    // eslint-disable-next-line no-console
    console.log("[player]", type, payload);
  }, []);

  const formatTime = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const t = Math.max(0, Math.floor(s));
    const mm = Math.floor(t / 60);
    const ss = String(t % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_IDLE_MS);
  }, []);

  const showToast = useCallback((label, icon) => {
    setToast({ key: `${label}-${Date.now()}`, label, icon });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const seekBy = useCallback((delta) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      const next = (v.currentTime || 0) + delta;
      const max = v.duration || Infinity;
      v.currentTime = Math.max(0, Math.min(next, max));
    } catch {
      // no-op
    }
  }, []);

  const playIfPaused = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play()
        .then(() =>
          showToast("Playing", <PlayArrowIcon sx={{ fontSize: TV_FS.toastIcon }} />)
        )
        .catch(() => {});
    } else {
      v.pause();
      showToast("Paused", <PauseIcon sx={{ fontSize: TV_FS.toastIcon }} />);
    }
  }, [showToast]);

  const handleIntroChoice = useCallback(
    (choice) => {
      logPlayerEvent("intro_choice", { choice });
      setShowIntro(false);
      setTimeout(() => {
        playIfPaused();
        showToast("Playing", <PlayArrowIcon sx={{ fontSize: TV_FS.toastIcon }} />);
      }, 0);
    },
    [logPlayerEvent, playIfPaused, showToast]
  );

  // Load user prefs
  useEffect(() => {
    const stop = onAuthStateChanged(auth, async (user) => {
      try {
        if (user?.uid) {
          const p = await fetchUserTvPrefs(user.uid);
          setPrefs(p);
        } else {
          setPrefs(DEFAULT_TV_PREFS);
        }
      } catch (e) {
        console.error("[player] failed to load tv prefs:", e);
        setPrefs(DEFAULT_TV_PREFS);
      }
    });
    return () => stop();
  }, []);

  // Wire time updates, autoplay, and end behavior
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = !!startMuted;
    v.loop = !!prefs.loopVideos;

    const onLoaded = () => {
      setTime((t) => ({ ...t, duration: v.duration || 0 }));
    };
    const onTime = () => {
      setTime({ current: v.currentTime || 0, duration: v.duration || 0 });
    };

    const onEnded = () => {
      if (!prefs.loopVideos) {
        if (returnTo) setScene(returnTo);
        else if (Array.isArray(stack) && stack.length > 1) popScene();
        else setScene({ name: "home" });
      }
    };

    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnded);

    if (showIntro) {
      v.pause();
    } else if (forceAutoplay) {
      v.play().catch(() => {
        v.muted = true;
        v.play().catch(() => {});
      });
    }

    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    src,
    showIntro,
    forceAutoplay,
    startMuted,
    prefs.loopVideos,
    returnTo,
    setScene,
    stack,
    popScene,
  ]);

  /**
   * Map keyboard / remote event to seek direction.
   * Supports:
   *  - Left arrow keys
   *  - Right arrow keys
   *  - Fire TV Rewind (KEYCODE_MEDIA_REWIND = 227) via keyCode 227
   *  - Fire TV Fast Forward (KEYCODE_MEDIA_FAST_FORWARD = 228) via keyCode 228
   */
  const getSeekDirectionFromEvent = (e) => {
    const k = e.key;
    const code = typeof e.keyCode === "number" ? e.keyCode : e.which;

    // Rewind
    if (
      k === "ArrowLeft" ||
      k === "Left" ||
      k === "MediaRewind" ||
      code === 37 ||
      code === 227
    ) {
      return -1;
    }

    // Fast forward
    if (
      k === "ArrowRight" ||
      k === "Right" ||
      k === "MediaFastForward" ||
      code === 39 ||
      code === 228
    ) {
      return 1;
    }

    return 0;
  };

  /**
   * Handle keydown for seek / ramp behavior.
   * Single press: 5s with x1 toast.
   * Hold: ramps x1 to x2 to x3 to x4 based on duration.
   * Toast only updates when multiplier changes.
   */
  const handleSeekKeyDown = useCallback(
    (e) => {
      const dir = getSeekDirectionFromEvent(e);
      if (!dir) return false;
      if (showIntro) return false;

      const hold = seekHoldRef.current;
      const now =
        typeof e.timeStamp === "number" && e.timeStamp > 0
          ? e.timeStamp
          : Date.now();

      // Fresh press
      if (!e.repeat) {
        hold.active = true;
        hold.dir = dir;
        hold.startTs = now;
        hold.lastMultiplier = 1;

        seekBy(dir * SEEK_STEP);
        showControls();

        showToast(
          "x1",
          dir > 0 ? (
            <FastForwardIcon sx={{ fontSize: TV_FS.toastIcon }} />
          ) : (
            <FastRewindIcon sx={{ fontSize: TV_FS.toastIcon }} />
          )
        );

        return true;
      }

      // Repeats while held
      if (!hold.active || hold.dir !== dir) {
        hold.active = true;
        hold.dir = dir;
        hold.startTs = now;
        hold.lastMultiplier = 1;
      }

      const elapsed = now - hold.startTs;

      let multiplier = 1;
      if (elapsed >= SEEK_RAMP_THRESHOLDS.x4) {
        multiplier = 4;
      } else if (elapsed >= SEEK_RAMP_THRESHOLDS.x3) {
        multiplier = 3;
      } else if (elapsed >= SEEK_RAMP_THRESHOLDS.x2) {
        multiplier = 2;
      }

      if (multiplier !== hold.lastMultiplier) {
        showToast(
          `x${multiplier}`,
          dir > 0 ? (
            <FastForwardIcon sx={{ fontSize: TV_FS.toastIcon }} />
          ) : (
            <FastRewindIcon sx={{ fontSize: TV_FS.toastIcon }} />
          )
        );
        hold.lastMultiplier = multiplier;
      }

      seekBy(dir * SEEK_STEP * multiplier);
      showControls();
      return true;
    },
    [seekBy, showControls, showToast, showIntro]
  );

  /**
   * Handle keyup to stop the hold / ramp.
   */
  const handleSeekKeyUp = useCallback((e) => {
    const dir = getSeekDirectionFromEvent(e);
    if (!dir) return false;

    const hold = seekHoldRef.current;
    if (hold.active && hold.dir === dir) {
      hold.active = false;
      hold.dir = 0;
      hold.startTs = 0;
      hold.lastMultiplier = 1;
      return true;
    }
    return false;
  }, []);

  /**
   * Detect media Play/Pause key.
   * For Fire TV web apps this is typically keyCode 179.
   */
  const isPlayPauseKey = (e) => {
    const k = e.key;
    const code = typeof e.keyCode === "number" ? e.keyCode : e.which;
    if (k === "MediaPlayPause") return true;
    if (code === 179) return true;
    return false;
  };

  /**
   * Handle Play/Pause media key.
   * Only active in this scene so it does not affect other pages.
   */
  const handlePlayPauseKeyDown = useCallback(
    (e) => {
      if (!isPlayPauseKey(e)) return false;
      if (showIntro) return false;
      togglePlay();
      showControls();
      return true;
    },
    [togglePlay, showControls, showIntro]
  );

  // Scene key handlers wiring (seek ramp and media play/pause)
  useEffect(() => {
    setSceneHandlers({
      onKeyDown: (e) => {
        if (handleSeekKeyDown(e) === true) return true;
        if (handlePlayPauseKeyDown(e) === true) return true;
        return false;
      },
      onKeyUp: (e) => {
        const handled = handleSeekKeyUp(e);
        return handled === true;
      },
      onEnter: () => {
        if (showIntro) return false;
        togglePlay();
        showControls();
        return true;
      },
      onUp: () => {
        showControls();
        return true;
      },
      onDown: () => {
        showControls();
        return true;
      },
      onBack: () => {
        if (Array.isArray(stack) && stack.length > 1) {
          popScene();
          return true;
        }
        if (returnTo) {
          setScene(returnTo);
          return true;
        }
        setScene({ name: "home" });
        return true;
      },
    });

    // Capture current ref object for cleanup to satisfy eslint
    const hold = seekHoldRef.current;

    return () => {
      setSceneHandlers({});
      if (hold) {
        hold.active = false;
        hold.dir = 0;
        hold.startTs = 0;
        hold.lastMultiplier = 1;
      }
    };
  }, [
    handleSeekKeyDown,
    handleSeekKeyUp,
    handlePlayPauseKeyDown,
    showIntro,
    togglePlay,
    showControls,
    stack,
    popScene,
    returnTo,
    setScene,
  ]);

  // Show controls on pointer move
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = () => showControls();
    el.addEventListener("mousemove", onMove, { passive: true });
    return () => el.removeEventListener("mousemove", onMove);
  }, [showControls]);

  // Cleanup timers on unmount
  useEffect(
    () => () => {
      clearTimeout(hideTimerRef.current);
      clearTimeout(toastTimerRef.current);
    },
    []
  );

  return (
    <Box
      ref={containerRef}
      className="scene"
      data-rails-root
      sx={{
        position: "relative",
        height: "100vh",
        width: "100vw",
        bgcolor: "black",
        overflow: "hidden",
      }}
    >
      {/* Video */}
      <video
        key={src}
        ref={videoRef}
        src={src}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        playsInline
        loop={!!prefs.loopVideos}
        muted={startMuted}
        autoPlay={forceAutoplay && !showIntro}
        controls={false}
        onClick={() => {
          if (!showIntro) {
            togglePlay();
            showControls();
          }
        }}
      />

      {/* Intro overlay (hidden when DISABLE_INTERACTIVE_MODAL === true) */}
      {showIntro && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            p: 2,
            zIndex: 3,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 100%)",
          }}
        >
          <Card sx={{ width: `min(92vw, ${TV_FS.cardMaxW}px)` }}>
            <CardContent sx={{ p: 4 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                alignItems="center"
              >
                <Box sx={{ flex: "0 0 auto" }}>
                  <QRPlaceholder text="Scan to interact" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      mb: 1,
                      fontSize: `${TV_FS.modalTitle}px`,
                      lineHeight: 1.1,
                    }}
                  >
                    Interact with this video
                  </Typography>

                  <Typography
                    sx={{
                      color: "text.secondary",
                      mb: 2.5,
                      fontSize: `${TV_FS.modalBody}px`,
                      lineHeight: 1.45,
                    }}
                  >
                    Scan the QR code to react live, add tags, attach
                    behind-the-scenes, or drop your own content into this
                    stream. Your contributions can appear as interactive
                    moments for everyone watching.
                  </Typography>

                  <Stack direction="row" spacing={1.5}>
                    <Focusable row={0} col={0} autoFocus>
                      <Button
                        variant="contained"
                        onClick={() => handleIntroChoice("interact")}
                        startIcon={<PlayArrowIcon />}
                        data-click="true"
                      >
                        Start watching
                      </Button>
                    </Focusable>

                    <Focusable row={0} col={1}>
                      <Button
                        variant="outlined"
                        onClick={() => handleIntroChoice("skip")}
                        startIcon={<CloseIcon />}
                        data-click="true"
                      >
                        Not now
                      </Button>
                    </Focusable>
                  </Stack>

                  <Typography
                    sx={{
                      mt: 1.5,
                      color: "text.secondary",
                      fontSize: `${TV_FS.modalHint}px`,
                      letterSpacing: 0.25,
                    }}
                    variant="body2"
                  >
                    Use ◄/► to choose • Enter to select
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Bottom progress-only control bar (auto-hides) */}
      {!showIntro && controlsVisible && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            gridTemplateRows: "1fr auto",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.45) 100%)",
            }}
          />
          <Box sx={{ px: 10, pb: 3, alignSelf: "end" }}>
            <LinearProgress
              variant="determinate"
              value={
                time.duration > 0
                  ? Math.min(100, (time.current / time.duration) * 100)
                  : 0
              }
              sx={{
                height: 8,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.18)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: "999px",
                },
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography
                sx={{
                  fontSize: `${TV_FS.timeFont}px`,
                  fontWeight: 800,
                  color: "rgba(255,255,255,0.95)",
                  textShadow:
                    "0 0 6px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.6)",
                }}
              >
                {formatTime(time.current)}
              </Typography>
              <Typography
                sx={{
                  fontSize: `${TV_FS.timeFont}px`,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.8)",
                  textShadow:
                    "0 0 6px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.6)",
                }}
              >
                {formatTime(time.duration)}
              </Typography>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Center action toast */}
      <Fade in={!!toast}>
        <Box
          aria-hidden={!toast}
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
            zIndex: 4,
          }}
        >
          {toast && (
            <Paper
              elevation={6}
              sx={{
                px: 3,
                py: 2,
                borderRadius: "16px",
                bgcolor: "rgba(0,0,0,0.78)",
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 1.75,
              }}
            >
              {toast.icon}
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: `${TV_FS.toastLabel}px`,
                }}
              >
                {toast.label}
              </Typography>
            </Paper>
          )}
        </Box>
      </Fade>
    </Box>
  );
}
